import { Alert, AlertIcon, Box, Button, Flex, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type InviteEmailAccountOrganizationDto,
  useLookUpInviteEmailAccountQuery,
} from "../../../../../../autogen/bff-api";

const LOOKUP_DEBOUNCE_MS = 400;
const isWellFormedEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export interface RegisteredMember {
  organization: InviteEmailAccountOrganizationDto;
  personId: string;
}

export type RegisteredMemberInvite =
  | "newInvite"
  | "addToInvite"
  | "alreadyInvited";

export const registeredMemberInvite = (
  invites: { organizationId: string; persons?: { id: string }[] | null }[],
  { organization, personId }: RegisteredMember,
): RegisteredMemberInvite => {
  const existing = invites.find(
    (invite) => invite.organizationId === organization.id,
  );
  if (!existing) return "newInvite";
  return (existing.persons ?? []).some((person) => person.id === personId)
    ? "alreadyInvited"
    : "addToInvite";
};

export const RegisteredAccountHint = ({
  eventId,
  email,
  canInvite,
  onInvite,
  sendsAtOnce = false,
}: {
  eventId: string;
  email: string;
  canInvite: (organization: InviteEmailAccountOrganizationDto) => boolean;
  onInvite: (member: RegisteredMember) => void | Promise<void>;
  sendsAtOnce?: boolean;
}) => {
  const { t } = useTranslation();
  const [lookedUpEmail, setLookedUpEmail] = useState("");
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [failed, setFailed] =
    useState<InviteEmailAccountOrganizationDto | null>(null);
  const lookUp = useMemo(
    () =>
      debounce((value: string) => setLookedUpEmail(value), LOOKUP_DEBOUNCE_MS),
    [],
  );
  useEffect(() => {
    lookUp(email.trim());
  }, [email, lookUp]);
  useEffect(() => () => lookUp.cancel(), [lookUp]);

  const { data } = useLookUpInviteEmailAccountQuery(
    isWellFormedEmail(lookedUpEmail)
      ? { eventId, email: lookedUpEmail }
      : skipToken,
  );
  const personId = data?.personId;
  const offered = (data?.organizations ?? []).filter(canInvite);
  if (!personId || offered.length === 0 || lookedUpEmail !== email.trim())
    return null;

  const invite = async (organization: InviteEmailAccountOrganizationDto) => {
    setInvitingId(organization.id);
    setFailed(null);
    try {
      await onInvite({ organization, personId });
    } catch {
      setFailed(organization);
    } finally {
      setInvitingId(null);
    }
  };

  return (
    <Alert
      status="info"
      rounded="md"
      fontSize="sm"
      alignItems="flex-start"
      mt="1"
    >
      <AlertIcon />
      <Box>
        <Text>
          {t(
            "{{email}} already has a SourceMagnet account, as a member of {{companies}}.",
            {
              email: lookedUpEmail,
              companies: offered
                .map((organization) => organization.name)
                .join(", "),
            },
          )}
        </Text>
        {sendsAtOnce && (
          <Text mt="1">
            {t(
              "The procurement is published, so choosing a company sends its invitation at once.",
            )}
          </Text>
        )}
        <Flex gap="2" mt="2" wrap="wrap">
          {offered.map((organization) => (
            <Button
              key={organization.id}
              size="xs"
              variant="outline"
              colorScheme="teal"
              isLoading={invitingId === organization.id}
              isDisabled={invitingId !== null && invitingId !== organization.id}
              onClick={() => void invite(organization)}
            >
              {t("Invite {{company}}", { company: organization.name })}
            </Button>
          ))}
        </Flex>
        {failed && (
          <Text color="smError" mt="1">
            {t(
              "{{company}} could not be invited. Try again, or keep the email invitation.",
              {
                company: failed.name,
              },
            )}
          </Text>
        )}
        <Text color="smMuted" mt="1">
          {t("Or keep the email invitation as it is.")}
        </Text>
      </Box>
    </Alert>
  );
};
